import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown, ChevronDown, ChevronRight, FileText } from "lucide-react";
import { useEffect } from "react";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import { useEntity } from "@/hooks/useEntity";

interface FeeEntity {
  entityName: string;
  entityId: string;
  instrumentName?: string;
  instrumentTicker?: string;
  instrumentType?: string;
  openDate?: string;
  ytdFees: {
    management: number;
    performance: number;
    expenseRatio: number;
    custody: number;
    fx: number;
    other: number;
    total: number;
  };
  inceptionFees: {
    management: number;
    performance: number;
    expenseRatio: number;
    custody: number;
    fx: number;
    other: number;
    total: number;
  };
  currentValue: number;
  ytdFeePercent: number;
  inceptionFeePercent: number;
  transactions: Array<{ date: string; type: string; description?: string; amount: number }>;
}

interface FeeTotals {
  ytd: number;
  inception: number;
  ytdManagement: number;
  ytdPerformance: number;
  ytdExpenseRatio: number;
  ytdCustody: number;
  ytdFx: number;
  ytdOther: number;
  totalAUM: number;
}

export default function Fees() {
  const { currentOrg, familyId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [breakdown, setBreakdown] = useState<FeeEntity[]>([]);
  const [totals, setTotals] = useState<FeeTotals | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [asOf] = useState(new Date().toISOString().split('T')[0]);
  const [entityId, setEntityId] = useState<string | null>(null);
  const { entities } = useEntity();
  
  const selectedEntity = entities.find(e => e.id === entityId);

  useEffect(() => {
    if (currentOrg?.id) {
      loadFees();
    }
  }, [currentOrg?.id, familyId, entityId]);

  const loadFees = async () => {
    if (!currentOrg?.id) return;

    setLoading(true);
    try {
      const yearStart = `${new Date().getFullYear()}-01-01`;
      
      const { data, error } = await supabase.functions.invoke('calculate-fees', {
        body: {
          orgId: currentOrg.id,
          familyId: familyId || undefined,
          entityId: entityId || undefined,
          periodStart: yearStart,
          periodEnd: asOf,
        },
      });

      if (error) throw error;

      setBreakdown(data.breakdown || []);
      setTotals(data.totals);
    } catch (error) {
      console.error('Error loading fees:', error);
      toast.error('Failed to load fee analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex-1 space-y-4 p-8">
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-4 md:grid-cols-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const ytdFeePercent = totals && totals.totalAUM > 0 
    ? (totals.ytd / totals.totalAUM) * 100 
    : 0;

  return (
    <div className="flex-1 space-y-4 p-8">
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Fee Analytics</h1>
            {selectedEntity && (
              <Badge variant="secondary">{selectedEntity.name}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Comprehensive fee analysis across all positions and managers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EntitySwitcher value={entityId} onValueChange={setEntityId} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Fees YTD</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals?.ytd || 0)}</div>
            <p className="text-xs text-muted-foreground">{formatPercent(ytdFeePercent)} of AUM</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals?.ytdManagement || 0)}</div>
            <p className="text-xs text-muted-foreground">Explicit fees</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expense Ratios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals?.ytdExpenseRatio || 0)}</div>
            <p className="text-xs text-muted-foreground">ETF/Fund fees</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals?.ytdPerformance || 0)}</div>
            <p className="text-xs text-muted-foreground">Carry & incentive</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Other Fees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency((totals?.ytdCustody || 0) + (totals?.ytdFx || 0) + (totals?.ytdOther || 0))}
            </div>
            <p className="text-xs text-muted-foreground">Custody, FX, etc.</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Fee Breakdown by Position & Manager</CardTitle>
          <CardDescription>
            Click to expand and view fee details and supporting documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Position / Manager</TableHead>
                <TableHead className="text-right">Current Value</TableHead>
                <TableHead className="text-right">YTD Fees</TableHead>
                <TableHead className="text-right">YTD %</TableHead>
                <TableHead className="text-right">Since Inception</TableHead>
                <TableHead className="text-right">Inception %</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {breakdown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No fee data available for the selected period
                  </TableCell>
                </TableRow>
              ) : (
                breakdown.map((entity) => {
                  const isExpanded = expanded === entity.entityId;
                  return (
                    <>
                      <TableRow
                        key={entity.entityId}
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() => setExpanded(isExpanded ? null : entity.entityId)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {entity.instrumentName || entity.entityName}
                              </span>
                              {entity.instrumentTicker && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {entity.instrumentTicker}
                                </span>
                              )}
                              {entity.instrumentType && (
                                <Badge variant="outline" className="text-xs w-fit mt-1">
                                  {entity.instrumentType}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(entity.currentValue)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(entity.ytdFees.total)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">
                            {formatPercent(entity.ytdFeePercent)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(entity.inceptionFees.total)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline">
                            {formatPercent(entity.inceptionFeePercent)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {entity.transactions.length > 0 && (
                            <Badge variant="secondary" className="gap-1">
                              <FileText className="h-3 w-3" />
                              {entity.transactions.length}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={7}>
                            <div className="py-4 space-y-4">
                              {/* Fee Type Breakdown */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                  <div className="text-xs text-muted-foreground">Management Fees (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.management)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Performance Fees (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.performance)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Expense Ratio (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.expenseRatio)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Custody Fees (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.custody)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">FX Fees (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.fx)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Other Fees (YTD)</div>
                                  <div className="text-sm font-semibold">{formatCurrency(entity.ytdFees.other)}</div>
                                </div>
                              </div>

                              {/* Transaction History */}
                              {entity.transactions.length > 0 && (
                                <div className="mt-4">
                                  <h4 className="text-sm font-medium mb-2">Fee Transactions</h4>
                                  <div className="space-y-2">
                                    {entity.transactions.map((txn, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-sm border-l-2 border-primary/20 pl-3 py-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-muted-foreground">
                                            {new Date(txn.date).toLocaleDateString()}
                                          </span>
                                          <Badge variant="outline" className="text-xs">
                                            {txn.type}
                                          </Badge>
                                          {txn.description && (
                                            <span className="text-xs text-muted-foreground truncate max-w-xs">
                                              {txn.description}
                                            </span>
                                          )}
                                        </div>
                                        <span className="font-mono font-semibold">
                                          {formatCurrency(txn.amount)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Inception Details */}
                              {entity.openDate && (
                                <div className="mt-2 text-xs text-muted-foreground">
                                  Position opened: {new Date(entity.openDate).toLocaleDateString()}
                                  {' • '}
                                  Total fees since inception: {formatCurrency(entity.inceptionFees.total)}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })
              )}

              {/* Total Row */}
              {totals && breakdown.length > 0 && (
                <TableRow className="border-t-2 bg-primary/5 font-bold">
                  <TableCell>Total Fees</TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.totalAUM)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.ytd)}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="default">{formatPercent(ytdFeePercent)}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.inception)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
